import { fuzzyMatchProduct } from './emailParser';

export interface ParsedAddress {
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
}

export interface ParsedItem {
  rawText: string;
  quantity: number;
  recipeName: string;
  recipeId: string;
  matchedRecipe?: { id: string; name: string; similarity: number };
  confidence: 'high' | 'medium' | 'low';
}

export interface ParsedOrderEmail {
  customer: {
    name: string;
    email: string;
    phone: string;
    streetAddress: string;
    addressLine2: string;
    city: string;
    province: string;
    postalCode: string;
  };
  items: ParsedItem[];
  deliveryType: 'Delivery' | 'Pickup';
  errors: string[];
}

/**
 * Normalize marker text for flexible matching
 * Handles variations like "Name:", "Name:  ", "NAME", etc.
 */
function normalizeMarker(text: string): string {
  return text
    .trim() // Remove leading/trailing whitespace
    .replace(/[:\s]+$/, '') // Remove trailing colons and spaces
    .toLowerCase();
}

/**
 * Extract text between two section markers
 * Handles both inline format (Name   Beverly Friesen Email) and line-based format
 */
export function extractSection(text: string, startMarker: string, endMarker: string): string {
  console.log('[extractSection] Extracting between:', startMarker, 'and', endMarker);

  // FIRST: Try collapsed text pattern where markers have no whitespace separation
  // (e.g., "dozenName Beverly FriesenEmail hjrdyck@gmail.com")
  // Match: any text, then startMarker (case-insensitive), then content (non-greedy),
  // then endMarker (case-insensitive), then any text
  // Use a case-insensitive search to find the markers
  const collapsedPattern = new RegExp(`${startMarker}\\s+(.+?)\\s+${endMarker}`, 'i');
  const collapsedMatch = text.match(collapsedPattern);

  if (collapsedMatch) {
    const extracted = collapsedMatch[1].trim();
    console.log('[extractSection] Found collapsed/inline pattern, extracted:', extracted);
    return extracted;
  }

  // SECOND: Try truly collapsed (no space) pattern
  // Handles "dozenName Beverly FriesenEmail" where Name and Email are directly adjacent to other text
  // This uses a broader match that doesn't require word boundaries
  const noSpacePattern = new RegExp(`${startMarker}([^]*?)${endMarker}`, 'i');
  const noSpaceMatch = text.match(noSpacePattern);

  if (noSpaceMatch) {
    const extracted = noSpaceMatch[1].trim();
    // Only accept this if the extracted content looks reasonable (has letters/spaces)
    if (extracted && /[a-zA-Z\s]/.test(extracted) && extracted.length > 0) {
      console.log('[extractSection] Found no-space pattern, extracted:', extracted);
      return extracted;
    }
  }

  // FALLBACK: Use line-based extraction for properly formatted emails
  const normalizedStartMarker = startMarker.toLowerCase();
  const normalizedEndMarker = endMarker.toLowerCase();

  const lines = text.split(/\r?\n/);
  const startIdx = lines.findIndex(line => normalizeMarker(line) === normalizedStartMarker);
  const endIdx = lines.findIndex((line, idx) => idx > startIdx && normalizeMarker(line) === normalizedEndMarker);

  console.log('[extractSection] Inline pattern not found, trying line-based extraction');
  console.log('[extractSection] startIdx:', startIdx, 'endIdx:', endIdx);

  if (startIdx === -1) {
    console.log(
      '[extractSection] Start marker not found. Lines checked:',
      lines
        .slice(0, 10)
        .map((line, idx) => `${idx}: "${line}" -> normalized: "${normalizeMarker(line)}"`)
        .join(', ')
    );
    return '';
  }

  if (endIdx === -1) {
    console.log('[extractSection] End marker not found, returning empty string');
    return '';
  }

  const extracted = lines
    .slice(startIdx + 1, endIdx)
    .map(line => line.trim())
    .filter(line => line.length > 0) // More explicit than .filter(line => line)
    .join('\n')
    .trim();

  console.log('[extractSection] Extracted:', extracted);
  return extracted;
}

/**
 * Extract quantity from text with various formats
 */
export function extractQuantity(text: string): { quantity: number; remainingText: string } {
  const patterns = [
    /(\d+)\s*(?:dozen|doz|dozn)\s*/i, // "3 dozen", "4 doz"
    /quantity\s+(\d+)\s*/i, // "quantity 6" (with space)
    /^(\d+)\.\s*/, // "1. " or "2. " (numbered list)
    /^(\d+)\s+(bags?)\s*/i, // "2 bags" or "1 bag"
    /^(\d+)\s+/ // "4 meatbuns"
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const quantity = parseInt(match[1], 10);
      const remainingText = text.replace(match[0], '').trim();
      return { quantity, remainingText };
    }
  }

  return { quantity: 0, remainingText: text };
}

/**
 * Check if a line is conversational text that should be ignored
 */
export function isConversationalLine(line: string): boolean {
  const lower = line.toLowerCase();

  const conversationalPatterns = [
    /^hi\b/i,
    /^hello\b/i,
    /^dear\b/i,
    /\bi got your contact\b/i,
    /\bis it still possible\b/i,
    /\bi was hoping\b/i,
    /\bthanks\b/i,
    /\bthank you\b/i,
    /\bwith delivery\b/i,
    /\bplease\b$/i,
    /\?$/ // Questions
  ];

  return conversationalPatterns.some(pattern => pattern.test(lower));
}

/**
 * Preprocess recipe text for better matching
 */
function preprocessRecipeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/\bno meat\b/g, '')
    .replace(/\band\b/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse address from formatted text
 */
export function parseAddress(addressText: string): ParsedAddress {
  const lines = addressText
    .split('\n')
    .map(l => l.trim())
    .filter(l => l);

  if (lines.length === 0) {
    return {
      streetAddress: '',
      addressLine2: '',
      city: '',
      province: '',
      postalCode: ''
    };
  }

  const streetAddress = lines[0] || '';
  const cityLine = lines[lines.length - 1] || '';

  // Match: "Winnipeg, Manitoba R3T0C8" or "Winnipeg, Manitoba R3T 0C8"
  const match = cityLine.match(/^([^,]+),\s*([^,\s]+)\s+([A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/);

  if (match) {
    const postalCode = match[3]
      .replace(/\s/g, '')
      .toUpperCase()
      .replace(/^(.{3})(.{3})$/, '$1 $2');

    return {
      streetAddress,
      addressLine2: '',
      city: match[1].trim(),
      province: match[2].trim(),
      postalCode
    };
  }

  // Fallback if pattern doesn't match
  return {
    streetAddress,
    addressLine2: '',
    city: '',
    province: '',
    postalCode: ''
  };
}

/**
 * Extract email address from text
 */
function extractEmail(text: string): string {
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
  const match = text.match(emailRegex);
  return match ? match[1] : '';
}

/**
 * Extract phone number from text
 */
function extractPhone(text: string): string {
  // Match formats like (204) 123-4567, 204-123-4567, etc.
  const phoneRegex = /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
  const match = text.match(phoneRegex);
  return match ? match[0] : text.trim();
}

/**
 * Detect delivery type from order text
 */
function detectDeliveryType(orderText: string): 'Delivery' | 'Pickup' {
  const lower = orderText.toLowerCase();

  if (lower.includes('pickup') || lower.includes('pick up')) {
    return 'Pickup';
  }

  // Default to Delivery
  return 'Delivery';
}

/**
 * Normalize email format to handle inline labels
 * Converts "Label   value" to "Label\nvalue" for consistent parsing
 */
function normalizeEmailFormat(emailText: string): string {
  const labels = ['Your Order', 'Name', 'Email', 'Phone', 'Address'];
  let normalized = emailText;

  for (const label of labels) {
    // Match: "Label" followed by 1+ spaces/tabs, then content
    const regex = new RegExp(`(${label})\\s+(.+)`, 'g');
    normalized = normalized.replace(regex, `$1\n$2`);
  }

  return normalized;
}

/**
 * Parse order items from order text
 */
export function parseOrderItems(
  orderText: string,
  availableRecipes: Array<{ id: string; name: string }>
): ParsedItem[] {
  const lines = orderText
    .split('\n')
    .map(l => l.trim())
    .filter(l => l);

  const items: ParsedItem[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip conversational lines
    if (isConversationalLine(line)) {
      i++;
      continue;
    }

    // Try to extract quantity from current line
    let { quantity, remainingText } = extractQuantity(line);

    // If no quantity found, check next line (multi-line case)
    if (quantity === 0 && i + 1 < lines.length) {
      const nextLine = lines[i + 1];
      const nextQuantity = extractQuantity(nextLine);
      if (nextQuantity.quantity > 0) {
        quantity = nextQuantity.quantity;
        remainingText = line; // Recipe name is on current line
        i++; // Skip next line since we used it
      }
    }

    // If still no quantity, skip this line
    if (quantity === 0) {
      i++;
      continue;
    }

    // Preprocess recipe name and fuzzy match
    const preprocessedName = preprocessRecipeText(remainingText);
    const matched = fuzzyMatchProduct(preprocessedName, availableRecipes, 0.6);

    const item: ParsedItem = {
      rawText: line,
      quantity,
      recipeName: remainingText,
      recipeId: matched?.id || '',
      matchedRecipe: matched || undefined,
      confidence: matched ? (matched.similarity > 0.8 ? 'high' : 'medium') : 'low'
    };

    items.push(item);
    i++;
  }

  return items;
}

/**
 * Main parsing function to extract order data from email text
 */
export function parseOrderEmail(
  emailText: string,
  availableRecipes: Array<{ id: string; name: string }>
): ParsedOrderEmail {
  const errors: string[] = [];

  // Normalize email format to handle inline labels
  const normalizedEmail = normalizeEmailFormat(emailText);

  // Extract sections
  const orderSection = extractSection(normalizedEmail, 'Your Order', 'Name');
  const nameSection = extractSection(normalizedEmail, 'Name', 'Email');
  const emailSection = extractSection(normalizedEmail, 'Email', 'Phone');
  const phoneSection = extractSection(normalizedEmail, 'Phone', 'Address');
  const addressSection = extractSection(normalizedEmail, 'Address', 'Canada');

  // Parse customer info
  const parsedAddress = parseAddress(addressSection);
  const customer = {
    name: nameSection.trim(),
    email: extractEmail(emailSection),
    phone: extractPhone(phoneSection),
    ...parsedAddress
  };

  console.log(
    '[parseOrderEmail] Extracted customer name:',
    customer.name ? `"${customer.name}" (length: ${customer.name.length})` : '(empty)'
  );

  // Parse items
  const items = parseOrderItems(orderSection, availableRecipes);

  // Detect delivery type
  const deliveryType = detectDeliveryType(orderSection);

  // Validate and collect errors
  if (!customer.name) errors.push('Missing customer name');
  if (!customer.email) errors.push('Missing email');
  if (!customer.phone) errors.push('Missing phone');
  if (!customer.streetAddress) errors.push('Missing street address');
  if (!customer.city) errors.push('Missing city');
  if (!customer.province) errors.push('Missing province');
  if (!customer.postalCode) errors.push('Missing postal code');
  if (items.length === 0) errors.push('No order items found');

  return {
    customer,
    items,
    deliveryType,
    errors
  };
}
